import { FC } from "react";

import { Stack } from "@mui/material";
import { RouteBuilders, isSignatureRequiredForGenerateReceipt } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { FormStepContainer } from "../FormStepContainer";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { Info } from "@/icons/outlined";

export type StepTaxReceiptFormData = {
  generateTaxReceipt: boolean;
};

export const DonationLiveEditorStepTaxReceipt: FC = () => {
  const { t } = useTranslate();
  const buildUrl = useUrlBuilder();
  const { apiFormObject: apiDonationForm } = useLiveFormEditorContext<"donation-form">();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const settingsUrl = buildUrl({
    route: RouteBuilders.buildSettingsPageLink({}),
    searchParams: { updateOrganizationInformation: "true" },
  });
  const form = useForm<StepTaxReceiptFormData>({
    defaultValues: {
      generateTaxReceipt: apiDonationForm.hasReceipt ?? true,
    },
  });

  const { control } = form;
  const { organization } = useCurrentUserContext();

  const shouldDisableToggle = !organization?.canGenerateReceipt;
  const shouldShowNoCharityNumberInfobox = !organization?.hasCharityNumber;
  const shouldShowNoSignatureInfobox =
    !organization?.signatureUrl &&
    organization?.hasCharityNumber &&
    isSignatureRequiredForGenerateReceipt(organization.country);

  const updateDonationFormHasReceipt = trpc.updateDonationFormHasReceipt.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepTaxReceiptFormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      const generateTaxReceipt = formData.generateTaxReceipt;
      return await updateDonationFormHasReceipt.mutateAsync({
        donationFormId: apiDonationForm.id,
        hasReceipt: generateTaxReceipt,
      });
    },
  });

  // Keep preview updated with the form data for tax receipt banner
  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.hasReceipt = formData.generateTaxReceipt;
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.title")}
      titleTooltip={t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.titleTooltip")}
      description={t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.description")}
      form={form}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleSaveAndNext,
      }}
    >
      <Stack direction="column" gap={2}>
        <LiveEditorBlock>
          <LiveEditorToggle
            label={t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.generateTaxReceiptLabel")}
            name="generateTaxReceipt"
            control={control}
            data-test="generate-tax-receipt-toggle"
            disabled={shouldDisableToggle}
            onValueChange={(checked) => {
              logUserActionEvent(
                checked ? AmplitudeEvents.GenerateTaxReceiptAdded : AmplitudeEvents.GenerateTaxReceiptRemoved
              );
            }}
          />
        </LiveEditorBlock>
        {shouldShowNoCharityNumberInfobox && (
          <DashboardInfobox
            icon={<Info />}
            vibe="brand"
            size="small"
            type="alert"
            inlineLinkProps={[
              {
                key: "settings",
                children: t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.goToSettings"),
                href: settingsUrl,
              },
            ]}
          >
            {t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.noCharityNumberInfobox")}
          </DashboardInfobox>
        )}
        {shouldShowNoSignatureInfobox && (
          <DashboardInfobox
            icon={<Info />}
            vibe="brand"
            size="small"
            type="alert"
            inlineLinkProps={[
              {
                key: "settings",
                children: t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.goToSettings"),
                href: settingsUrl,
              },
            ]}
          >
            {t("dashboard", "liveFormEditor.stepTaxReceipt.donationForm.noSignatureInfobox")}
          </DashboardInfobox>
        )}
      </Stack>
    </FormStepContainer>
  );
};
